import { useEffect } from "react";
import { useMe } from "../api/queries.js";
import type { MeResponse } from "../api/types.js";

/**
 * #3355 — the console's tab title in the tenant's branding.
 *
 * `index.html` carries the house title ("AR Dept operator console"). A signed-in person whose `/me`
 * carries their sole tenant's `branding.console_title` sees that instead; everyone else — every AR
 * Dept operator, every platform admin, any principal over more than one tenant (the engine sends no
 * branding to them) — keeps the house title. Pure, so it is tested without a DOM.
 */
export function brandedConsoleTitle(
  me: Pick<MeResponse, "is_platform_admin" | "allowed_tenant_ids" | "tenant_branding"> | undefined,
): string | null {
  if (!me || me.is_platform_admin || me.allowed_tenant_ids.length !== 1) return null;
  const title = me.tenant_branding?.console_title?.trim();
  return title ? title : null;
}

/** Wears {@link brandedConsoleTitle} as `document.title` while it applies; puts the previous title back. */
export function useBrandedConsoleTitle(): void {
  const me = useMe();
  const title = brandedConsoleTitle(me.data);
  useEffect(() => {
    if (title === null) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** Mount once inside the signed-in tree. Renders nothing. */
export function BrandedConsoleTitle(): null {
  useBrandedConsoleTitle();
  return null;
}
